import { useState, useEffect, useCallback } from 'react';

export const useScenarioEditor = (supabase, scenarioId) => {
  const isEditing = !!scenarioId;
  
  // State for form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    // These properties control the toggles for each section
    hasValidateLogic: false,
    hasCreateLogic: false,
    hasDisplayLogic: false,
    hasOfflineLogic: false,
    // The actual logic configuration
    validate_logic: {
      search_base: { id: '', name: '' },
      search_table: { id: '', name: '' },
      search_field: { id: '', name: '' },
      condition_groups: [
        { isStandalone: true, conditions: [] } // Standalone conditions
      ],
      main_operator: 'AND' // AND/OR between groups and standalone conditions
    },
    create_logic: {
      create_base: { id: '', name: '' },
      create_table: { id: '', name: '' },
      create_fields: []
    },
    display_logic: [],
    offline_logic: {
      last_modified_field: { id: '', name: '' },
      showRecordsList: false,
      displayFields: [],
      filterConditions: []
    }
  });

  // State for Airtable metadata
  const [airtableBases, setAirtableBases] = useState([]);
  const [airtableTables, setAirtableTables] = useState({
    validate: [],
    create: []
  });
  const [airtableFields, setAirtableFields] = useState({
    validate: [],
    create: []
  });

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingBases, setIsLoadingBases] = useState(false);
  const [isLoadingTables, setIsLoadingTables] = useState({ validate: false, create: false });
  const [isLoadingFields, setIsLoadingFields] = useState({ validate: false, create: false });
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Fetch scenario data from Supabase
  const fetchScenario = useCallback(async () => {
    if (!isEditing) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('scenarios')
        .select('*')
        .eq('id', scenarioId)
        .single();
        
      if (error) throw error;
      
      if (data) {
        // Initialize the form with the scenario data
        setFormData({
          name: data.name || '',
          description: data.description || '',
          token: data.token || '',
          hasValidateLogic: !!data.validate_logic,
          hasCreateLogic: !!data.create_logic,
          hasDisplayLogic: !!data.display_logic && data.display_logic.length > 0,
          hasOfflineLogic: !!(data.offline_logic && typeof data.offline_logic === 'object' && Object.keys(data.offline_logic).length > 0),
          validate_logic: (() => {
            const defaultLogic = {
              search_base: { id: '', name: '' },
              search_table: { id: '', name: '' },
              search_field: { id: '', name: '' },
              condition_groups: [
                { isStandalone: true, conditions: [] }
              ],
              main_operator: 'AND'
            };
            
            if (!data.validate_logic) return defaultLogic;
            
            // Migrate old format if needed
            if (data.validate_logic.conditions && !data.validate_logic.condition_groups) {
              return {
                ...defaultLogic,
                ...data.validate_logic,
                condition_groups: [
                  { isStandalone: true, conditions: data.validate_logic.conditions || [] }
                ],
                main_operator: data.validate_logic.logic_operator || 'AND'
              };
            }
            
            return { ...defaultLogic, ...data.validate_logic };
          })(),
          create_logic: data.create_logic || {
            create_base: { id: '', name: '' },
            create_table: { id: '', name: '' },
            create_fields: []
          },
          display_logic: data.display_logic || [],
          offline_logic: data.offline_logic || {
            last_modified_field: { id: '', name: '' }
          }
        });
        
        // If we have base IDs, fetch the tables for each section
        if (data.validate_logic?.search_base?.id) {
          fetchAirtableTables('validate', data.validate_logic.search_base.id);
        }
        
        if (data.create_logic?.create_base?.id) {
          fetchAirtableTables('create', data.create_logic.create_base.id);
        }
        
        // If we have table IDs, fetch the fields for each section
        if (data.validate_logic?.search_base?.id && data.validate_logic?.search_table?.id) {
          fetchAirtableFields(
            'validate', 
            data.validate_logic.search_base.id, 
            data.validate_logic.search_table.id
          );
        }
        
        if (data.create_logic?.create_base?.id && data.create_logic?.create_table?.id) {
          fetchAirtableFields(
            'create', 
            data.create_logic.create_base.id, 
            data.create_logic.create_table.id
          );
        }
      }
    } catch (error) {
      console.error('Error fetching scenario:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, scenarioId, supabase]);

  // Fetch Airtable bases from Supabase Edge Function
  const fetchAirtableBases = useCallback(async () => {
    try {
      setIsLoadingBases(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke('user-airtable-bases', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      
      if (error) throw error;
      
      setAirtableBases(data || []);
    } catch (error) {
      console.error('Error fetching Airtable bases:', error);
      setError(error.message);
    } finally {
      setIsLoadingBases(false);
    }
  }, [supabase]);

  // Fetch Airtable tables for a specific base
  const fetchAirtableTables = useCallback(async (section, baseId) => {
    if (!baseId) return;
    
    try {
      setIsLoadingTables(prev => ({ ...prev, [section]: true }));
      
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke('user-airtable-tables', {
        body: { base_id: baseId },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      
      if (error) throw error;
      
      setAirtableTables(prev => ({
        ...prev,
        [section]: data || []
      }));
    } catch (error) {
      console.error(`Error fetching Airtable tables for ${section}:`, error);
      setError(error.message);
    } finally {
      setIsLoadingTables(prev => ({ ...prev, [section]: false }));
    }
  }, [supabase]);

  // Fetch Airtable fields for a specific table
  const fetchAirtableFields = useCallback(async (section, baseId, tableId) => {
    if (!baseId || !tableId) return;
    
    try {
      setIsLoadingFields(prev => ({ ...prev, [section]: true }));
      
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke('user-airtable-fields', {
        body: { 
          base_id: baseId,
          table_id: tableId 
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      
      if (error) throw error;
      
      setAirtableFields(prev => ({
        ...prev,
        [section]: data || []
      }));
    } catch (error) {
      console.error(`Error fetching Airtable fields for ${section}:`, error);
      setError(error.message);
    } finally {
      setIsLoadingFields(prev => ({ ...prev, [section]: false }));
    }
  }, [supabase]);

  // Form validation
  const validateForm = useCallback(() => {
    const errors = {};
    
    // Basic validation
    if (!formData.name.trim()) {
      errors.name = 'Scenario name is required';
    }
    
    // Validate logic section validation
    if (formData.hasValidateLogic) {
      if (!formData.validate_logic.search_base.id) {
        errors.validateBase = 'Base is required for validation';
      }
      
      if (!formData.validate_logic.search_table.id) {
        errors.validateTable = 'Table is required for validation';
      }
      
      if (!formData.validate_logic.search_field.id) {
        errors.validateField = 'Lookup field is required for validation';
      }
    }
    
    // Create logic section validation
    if (formData.hasCreateLogic) {
      if (!formData.create_logic.create_base.id) {
        errors.createBase = 'Base is required for record creation';
      }
      
      if (!formData.create_logic.create_table.id) {
        errors.createTable = 'Table is required for record creation';
      }
      
      if (formData.create_logic.create_fields.length === 0) {
        errors.createFields = 'At least one field is required for record creation';
      }
      
      // Check if any create field uses validated_record type but validate logic is disabled
      const hasValidatedRecordField = formData.create_logic.create_fields.some(
        field => field.content.type === 'validated_record'
      );
      
      if (hasValidatedRecordField && !formData.hasValidateLogic) {
        errors.createFields = 'Validated Record Link requires Validate Logic to be enabled';
      }
    }
    
    // Display logic validation
    if (formData.hasDisplayLogic) {
      if (!formData.hasValidateLogic && !formData.hasCreateLogic) {
        errors.displayLogic = 'Display logic requires either validate or create logic to be enabled';
      }
      
      if (formData.display_logic.length === 0) {
        errors.displayFields = 'At least one display field is required';
      }
      
      // Check if display fields reference valid sections
      const hasInvalidRecords = formData.display_logic.some(item => {
        if (item.record === 'validate' && !formData.hasValidateLogic) return true;
        if (item.record === 'create' && !formData.hasCreateLogic) return true;
        return false;
      });
      
      if (hasInvalidRecords) {
        errors.displayRecords = 'Display fields reference disabled sections';
      }
    }

    // Offline logic validation
    if (formData.hasOfflineLogic) {
      if (!formData.hasValidateLogic) {
        errors.offlineLogic = 'Offline mode requires Validate Logic to be enabled';
      }
      
      if (!formData.offline_logic.last_modified_field.id) {
        errors.offlineLastModifiedField = 'Last modified field is required for offline mode';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // Save scenario
  const saveScenario = useCallback(async () => {
    if (!validateForm()) {
      return false;
    }
    
    try {
      setIsSaving(true);
      setError(null);
      
      // Prepare data for saving
      const { data: { user } } = await supabase.auth.getUser();
      const scenarioData = {
        name: formData.name,
        description: formData.description,
        token: formData.token,
        user_id: user?.id
      };

      // Add logic blocks if enabled
      if (formData.hasValidateLogic) {
        scenarioData.validate_logic = formData.validate_logic;
      } else {
        scenarioData.validate_logic = null;
      }
      
      if (formData.hasCreateLogic) {
        // If validate logic is disabled, ensure no create fields use validated_record type
        if (!formData.hasValidateLogic) {
          const createFields = formData.create_logic.create_fields.filter(
            field => field.content.type !== 'validated_record'
          );
          
          scenarioData.create_logic = {
            ...formData.create_logic,
            create_fields: createFields
          };
        } else {
          scenarioData.create_logic = formData.create_logic;
        }
      } else {
        scenarioData.create_logic = null;
      }
      
      if (formData.hasDisplayLogic) {
        scenarioData.display_logic = formData.display_logic;
      } else {
        scenarioData.display_logic = null;
      }
      
      if (formData.hasOfflineLogic) {
        scenarioData.offline_logic = formData.offline_logic;
      } else {
        scenarioData.offline_logic = null;
      }
      
      console.log('Saving scenario with user_id:', user?.id);
      console.log('Scenario data:', scenarioData);
      
      // Save to Supabase
      let result;
      
      if (isEditing) {
        // Update existing scenario
        result = await supabase
          .from('scenarios')
          .update(scenarioData)
          .eq('id', scenarioId)
          .select();
      } else {
        // Create new scenario
        result = await supabase
          .from('scenarios')
          .insert([scenarioData])
          .select();
      }
      
      const { error: saveError } = result;
      
      if (saveError) throw saveError;
      
      return true;
    } catch (error) {
      console.error('Error saving scenario:', error);
      setError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [formData, isEditing, scenarioId, supabase, validateForm]);

  // Delete scenario
  const deleteScenario = useCallback(async () => {
    if (!isEditing) return false;
    
    try {
      setIsLoading(true);
      
      const { error } = await supabase
        .from('scenarios')
        .delete()
        .eq('id', scenarioId);
      
      if (error) throw error;
      
      return true;
    } catch (error) {
      console.error('Error deleting scenario:', error);
      setError(error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isEditing, scenarioId, supabase]);

  // Form input updaters
  const updateFormInput = useCallback((name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error for this field if it exists
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Toggle section and handle dependencies
  const toggleSection = useCallback((section) => {
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [section]: !prev[section]
      };
      
      // If turning off validate logic, check for validated_record in create fields
      if (section === 'hasValidateLogic' && !newFormData.hasValidateLogic) {
        // Check if any create fields use validated_record
        const hasValidatedRecordField = prev.create_logic.create_fields.some(
          field => field.content.type === 'validated_record'
        );
        
        if (hasValidatedRecordField) {
          // Show validation error
          setValidationErrors(prevErrors => ({
            ...prevErrors,
            createFields: 'Cannot disable Validate Logic while using Validated Record Link fields'
          }));
          
          // Don't turn off validate logic
          return prev;
        }
      }
      
      return newFormData;
    });
  }, []);
  
  // Update validate base
  const updateValidateBase = useCallback((baseId, baseName) => {
    setFormData(prev => ({
      ...prev,
      validate_logic: {
        ...prev.validate_logic,
        search_base: { id: baseId, name: baseName },
        search_table: { id: '', name: '' },
        search_field: { id: '', name: '' },
        condition_groups: [
          { isStandalone: true, conditions: [] }
        ],
        main_operator: 'AND'
      }
    }));
    
    // Fetch tables for this base
    if (baseId) {
      fetchAirtableTables('validate', baseId);
    } else {
      setAirtableTables(prev => ({ ...prev, validate: [] }));
    }
    
    // Clear fields
    setAirtableFields(prev => ({ ...prev, validate: [] }));
  }, [fetchAirtableTables]);
  
  // Update validate table
  const updateValidateTable = useCallback((tableId, tableName) => {
    setFormData(prev => ({
      ...prev,
      validate_logic: {
        ...prev.validate_logic,
        search_table: { id: tableId, name: tableName },
        search_field: { id: '', name: '' },
        condition_groups: [
          { isStandalone: true, conditions: [] }
        ],
        main_operator: 'AND'
      }
    }));
    
    // Fetch fields for this table
    if (tableId && formData.validate_logic.search_base.id) {
      fetchAirtableFields('validate', formData.validate_logic.search_base.id, tableId);
    } else {
      setAirtableFields(prev => ({ ...prev, validate: [] }));
    }
  }, [fetchAirtableFields, formData.validate_logic.search_base.id]);
  
  // Update validate field
  const updateValidateField = useCallback((fieldId, fieldName) => {
    setFormData(prev => ({
      ...prev,
      validate_logic: {
        ...prev.validate_logic,
        search_field: { id: fieldId, name: fieldName }
      }
    }));
  }, []);
  
  // Add condition to match real Supabase structure
  const addValidationCondition = useCallback((condition, groupType = 'standalone') => {
    setFormData(prev => {
      const newConditionGroups = [...prev.validate_logic.condition_groups];
      
      if (groupType === 'standalone') {
        // Add to standalone group (isStandalone: true) - matches real structure
        let standaloneGroup = newConditionGroups.find(g => g.isStandalone);
        
        if (!standaloneGroup) {
          standaloneGroup = { isStandalone: true, conditions: [] };
          newConditionGroups.push(standaloneGroup);
        }
        
        standaloneGroup.conditions.push(condition);
      } else if (groupType === 'or') {
        // Add to OR group for "any" conditions
        let orGroup = newConditionGroups.find(g => g.operator === 'OR');
        
        if (!orGroup) {
          orGroup = { operator: 'OR', conditions: [] };
          newConditionGroups.push(orGroup);
        }
        
        orGroup.conditions.push(condition);
      }
      
      return {
        ...prev,
        validate_logic: {
          ...prev.validate_logic,
          condition_groups: newConditionGroups
        }
      };
    });
  }, []);
  
  // Remove condition - matching real Supabase structure
  const removeValidationCondition = useCallback((groupType, conditionIndex) => {
    try {
      setFormData(prev => {
      // Safety check
      if (!prev.validate_logic || !prev.validate_logic.condition_groups) {
        console.error('Invalid condition_groups structure');
        return prev;
      }
      
      const newConditionGroups = [...prev.validate_logic.condition_groups];
      
      if (groupType === 'standalone') {
        // Remove from standalone group (isStandalone: true)
        const standaloneGroup = newConditionGroups.find(g => g.isStandalone);
        
        if (standaloneGroup && standaloneGroup.conditions && Array.isArray(standaloneGroup.conditions)) {
          if (conditionIndex >= 0 && conditionIndex < standaloneGroup.conditions.length) {
            standaloneGroup.conditions.splice(conditionIndex, 1);
          } else {
            console.error('Invalid condition index:', conditionIndex, 'for conditions array of length:', standaloneGroup.conditions.length);
          }
        } else {
          console.error('Standalone group not found or invalid conditions array');
        }
      } else if (groupType === 'or') {
        // Remove from OR group
        const orGroup = newConditionGroups.find(g => g.operator === 'OR');
        
        if (orGroup && orGroup.conditions && Array.isArray(orGroup.conditions)) {
          if (conditionIndex >= 0 && conditionIndex < orGroup.conditions.length) {
            orGroup.conditions.splice(conditionIndex, 1);
          } else {
            console.error('Invalid condition index:', conditionIndex, 'for OR conditions array of length:', orGroup.conditions.length);
          }
        } else {
          console.error('OR group not found or invalid conditions array');
        }
      }
      
      return {
        ...prev,
        validate_logic: {
          ...prev.validate_logic,
          condition_groups: newConditionGroups
        }
      };
    });
    } catch (error) {
      console.error('Error removing validation condition:', error);
      setError('Failed to remove condition. Please try again.');
    }
  }, [setError]);

  // Update condition
  const updateValidationCondition = useCallback((groupIndex, conditionIndex, updates) => {
    setFormData(prev => {
      const newConditionGroups = [...prev.validate_logic.condition_groups];
      
      if (groupIndex === null) {
        // Update standalone condition
        const standaloneGroup = newConditionGroups.find(g => g.isStandalone);
        standaloneGroup.conditions[conditionIndex] = { 
          ...standaloneGroup.conditions[conditionIndex], 
          ...updates 
        };
      } else {
        // Update condition in specific group
        newConditionGroups[groupIndex].conditions[conditionIndex] = { 
          ...newConditionGroups[groupIndex].conditions[conditionIndex], 
          ...updates 
        };
      }
      
      return {
        ...prev,
        validate_logic: {
          ...prev.validate_logic,
          condition_groups: newConditionGroups
        }
      };
    });
  }, []);

  // Add new group
  const addValidationGroup = useCallback((operator = 'OR') => {
    setFormData(prev => ({
      ...prev,
      validate_logic: {
        ...prev.validate_logic,
        condition_groups: [
          ...prev.validate_logic.condition_groups,
          { operator, conditions: [] }
        ]
      }
    }));
  }, []);

  // Update group
  const updateValidationGroup = useCallback((groupIndex, updates) => {
    setFormData(prev => {
      const newConditionGroups = [...prev.validate_logic.condition_groups];
      newConditionGroups[groupIndex] = { ...newConditionGroups[groupIndex], ...updates };
      
      return {
        ...prev,
        validate_logic: {
          ...prev.validate_logic,
          condition_groups: newConditionGroups
        }
      };
    });
  }, []);

  // Remove group
  const removeValidationGroup = useCallback((groupIndex) => {
    setFormData(prev => ({
      ...prev,
      validate_logic: {
        ...prev.validate_logic,
        condition_groups: prev.validate_logic.condition_groups.filter((_, i) => i !== groupIndex)
      }
    }));
  }, []);

  // Move condition to group
  const moveConditionToGroup = useCallback((conditionIndex, targetGroupIndex) => {
    setFormData(prev => {
      const newConditionGroups = [...prev.validate_logic.condition_groups];
      const standaloneGroup = newConditionGroups.find(g => g.isStandalone);
      
      // Move condition from standalone to target group
      const condition = standaloneGroup.conditions.splice(conditionIndex, 1)[0];
      newConditionGroups[targetGroupIndex].conditions.push(condition);
      
      return {
        ...prev,
        validate_logic: {
          ...prev.validate_logic,
          condition_groups: newConditionGroups
        }
      };
    });
  }, []);

  // Update main operator
  const updateValidationMainOperator = useCallback((operator) => {
    setFormData(prev => ({
      ...prev,
      validate_logic: {
        ...prev.validate_logic,
        main_operator: operator
      }
    }));
  }, []);

  // Reorder condition within group
  const reorderValidationCondition = useCallback((groupIndex, fromIndex, toIndex) => {
    setFormData(prev => {
      const newConditionGroups = [...prev.validate_logic.condition_groups];
      
      if (groupIndex === null) {
        // Reorder in standalone conditions
        const standaloneGroup = newConditionGroups.find(g => g.isStandalone);
        const conditions = [...standaloneGroup.conditions];
        const [movedCondition] = conditions.splice(fromIndex, 1);
        conditions.splice(toIndex, 0, movedCondition);
        standaloneGroup.conditions = conditions;
      } else {
        // Reorder in specific group
        const conditions = [...newConditionGroups[groupIndex].conditions];
        const [movedCondition] = conditions.splice(fromIndex, 1);
        conditions.splice(toIndex, 0, movedCondition);
        newConditionGroups[groupIndex].conditions = conditions;
      }
      
      return {
        ...prev,
        validate_logic: {
          ...prev.validate_logic,
          condition_groups: newConditionGroups
        }
      };
    });
  }, []);

  // Reorder groups
  const reorderValidationGroup = useCallback((fromIndex, toIndex) => {
    setFormData(prev => {
      const conditionGroups = [...prev.validate_logic.condition_groups];
      const realGroups = conditionGroups.filter(g => !g.isStandalone);
      const standaloneGroup = conditionGroups.find(g => g.isStandalone);
      
      const [movedGroup] = realGroups.splice(fromIndex, 1);
      realGroups.splice(toIndex, 0, movedGroup);
      
      // Reconstruct the array with standalone group first
      const newConditionGroups = [standaloneGroup, ...realGroups];
      
      return {
        ...prev,
        validate_logic: {
          ...prev.validate_logic,
          condition_groups: newConditionGroups
        }
      };
    });
  }, []);
  
  // Update create base
  const updateCreateBase = useCallback((baseId, baseName) => {
    setFormData(prev => ({
      ...prev,
      create_logic: {
        ...prev.create_logic,
        create_base: { id: baseId, name: baseName },
        create_table: { id: '', name: '' },
        create_fields: []
      }
    }));
    
    // Fetch tables for this base
    if (baseId) {
      fetchAirtableTables('create', baseId);
    } else {
      setAirtableTables(prev => ({ ...prev, create: [] }));
    }
    
    // Clear fields
    setAirtableFields(prev => ({ ...prev, create: [] }));
  }, [fetchAirtableTables]);
  
  // Update create table
  const updateCreateTable = useCallback((tableId, tableName) => {
    setFormData(prev => ({
      ...prev,
      create_logic: {
        ...prev.create_logic,
        create_table: { id: tableId, name: tableName },
        create_fields: []
      }
    }));
    
    // Fetch fields for this table
    if (tableId && formData.create_logic.create_base.id) {
      fetchAirtableFields('create', formData.create_logic.create_base.id, tableId);
    } else {
      setAirtableFields(prev => ({ ...prev, create: [] }));
    }
  }, [fetchAirtableFields, formData.create_logic.create_base.id]);
  
  // Add create field
  const addCreateField = useCallback((field) => {
    // Check if field type is validated_record but validate logic is disabled
    if (field.content.type === 'validated_record' && !formData.hasValidateLogic) {
      setValidationErrors(prev => ({
        ...prev,
        createFields: 'Validated Record Link requires Validate Logic to be enabled'
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      create_logic: {
        ...prev.create_logic,
        create_fields: [...prev.create_logic.create_fields, field]
      }
    }));
  }, [formData.hasValidateLogic]);
  
  // Remove create field
  const removeCreateField = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      create_logic: {
        ...prev.create_logic,
        create_fields: prev.create_logic.create_fields.filter((_, i) => i !== index)
      }
    }));
  }, []);

  // Update override settings
  const updateOverrideSettings = useCallback((settings) => {
    setFormData(prev => ({
      ...prev,
      create_logic: {
        ...prev.create_logic,
        ...settings
      }
    }));
  }, []);

  // Add display field
  const addDisplayField = useCallback((field) => {
    setFormData(prev => ({
      ...prev,
      display_logic: [...prev.display_logic, field]
    }));
  }, []);
  
  // Remove display field
  const removeDisplayField = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      display_logic: prev.display_logic.filter((_, i) => i !== index)
    }));
  }, []);

  // Update offline last modified field
  const updateOfflineLastModifiedField = useCallback((fieldId, fieldName) => {
    setFormData(prev => ({
      ...prev,
      offline_logic: {
        ...prev.offline_logic,
        last_modified_field: { id: fieldId, name: fieldName }
      }
    }));
  }, []);

  // Update offline records list configuration
  const updateOfflineRecordsList = useCallback((updates) => {
    setFormData(prev => ({
      ...prev,
      offline_logic: {
        ...prev.offline_logic,
        ...updates
      }
    }));
  }, []);

  // Fetch data on initial load
  useEffect(() => {
    fetchAirtableBases();
    if (isEditing) {
      fetchScenario();
    }
  }, [fetchAirtableBases, fetchScenario, isEditing]);
  
  return {
    isEditing,
    formData,
    isLoading,
    isSaving,
    isLoadingBases,
    isLoadingTables,
    isLoadingFields,
    error,
    validationErrors,
    airtableBases,
    airtableTables,
    airtableFields,
    // Actions
    updateFormInput,
    toggleSection,
    updateValidateBase,
    updateValidateTable,
    updateValidateField,
    addValidationCondition,
    removeValidationCondition,
    updateValidationCondition,
    addValidationGroup,
    updateValidationGroup,
    removeValidationGroup,
    moveConditionToGroup,
    updateValidationMainOperator,
    reorderValidationCondition,
    reorderValidationGroup,
    updateCreateBase,
    updateCreateTable,
    addCreateField,
    removeCreateField,
    updateOverrideSettings,
    addDisplayField,
    removeDisplayField,
    updateOfflineLastModifiedField,
    updateOfflineRecordsList,
    saveScenario,
    deleteScenario,
    validateForm
  };
};